import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { getSearchParams, Dictionary } from '@tmap-web-lib/utils'
import { createElement, useCallback } from 'react'
import Url from 'url-parse'
import { gte } from 'semver'
import { ModalAlert } from '@/components'
import { LOG, useTmapLogContext } from '@/providers'
import { useSendEventLog } from '@/hooks'
import { useModalManager } from '@tmap-web-lib/modal-manager'

const PAGE_ID = LOG.APP_UPDATE.PAGE_ID

function useOpenService() {
  const modalManager = useModalManager()
  const { tmapLogClient } = useTmapLogContext()
  const sendEventLog = useSendEventLog()

  return useCallback((srcUrl: string) => {
    if (!srcUrl) {
      return
    }

    const urlObject = new Url(srcUrl)
    const scheme = urlObject.protocol
    const serviceName = urlObject.hostname

    const showUpdateModal = () => {
      tmapLogClient('expose').setPageId(PAGE_ID).send()
      modalManager.showModal({
        render: ({ close }) => (
          createElement(ModalAlert, {
            title: '서비스 이용을 위해\n앱을 업데이트 해주세요.',
            onOk: () => {
              sendEventLog({
                actionId: 'tap.confirm_update',
                pageId: PAGE_ID,
              })
              if (TmapApp.env.isAndroid) {
                TmapApp.openBrowser({ url: 'https://play.google.com/store/apps/details?id=com.skt.tmap.ku' })
              } else {
                TmapApp.openBrowser({ url: 'https://apps.apple.com/kr/app/티맵-장소추천-지도-운전점수-대중교통-대리/id431589174' })
              }
              close()
            },
            onCancel: () => {
              sendEventLog({ actionId: 'tap.close_update', pageId: PAGE_ID })
              close()
            },
            okText: '업데이트하기',
            cancelText: '닫기',
          })
        ),
      })
    }

    if (scheme.startsWith('tmap')) {
      if (srcUrl.includes('tmap://webview')) {
        if (gte(TmapApp.env.appVersion, '10.5.1')) {
          const searchParams = getSearchParams(urlObject.query || '')
          const params = new URLSearchParams(searchParams.params)
          const obj: Dictionary = {}

          for (const [key, value] of Array.from(params.entries())) {
            obj[key] = value
          }

          if (searchParams.service && searchParams.id) {
            TmapApp.openInAppBrowser({
              service: searchParams.service,
              id: searchParams.id,
              params: obj,
            })
          }
        } else {
          showUpdateModal()
        }
      } else if (srcUrl.includes('tmap://vertical')) {
        if (gte(TmapApp.env.appVersion, '10.9.0')) {
          const params = getSearchParams(urlObject.query || '')
          TmapApp.openServiceByName({ serviceName, jsonData: params })
        } else {
          showUpdateModal()
        }
      } else {
        // 대리 9.19부터 openServiceByName 사용 가능. 버전 분기 처리 하지 않는 것으로 논의
        const params = getSearchParams(urlObject.query || '')
        TmapApp.openServiceByName({ serviceName, jsonData: params })
      }
    } else {
      TmapApp.openBrowser({ url: srcUrl })
    }
  }, [modalManager, tmapLogClient, sendEventLog])
}

export { useOpenService }
